import { useEffect, useState, useCallback } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Persistent per-browser fingerprint so a user can update (not duplicate) their
 * rating from the same device. NOT cryptographic — just enough to debounce.
 */
const getFingerprint = () => {
  let fp = localStorage.getItem('masala-spin-fp');
  if (!fp) {
    fp =
      (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('masala-spin-fp', fp);
  }
  return fp;
};

/**
 * <RatingStars> — 5-star rating widget for a single recipe.
 * @param {string} recipeId
 * @param {(agg:{avg:number,count:number}) => void} onChange — fires after every load + submit
 */
export const RatingStars = ({ recipeId, onChange }) => {
  const [hover, setHover] = useState(0);
  const [my, setMy] = useState(0);
  const [agg, setAgg] = useState({ avg: 0, count: 0 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!recipeId) return;
    let cancelled = false;
    (async () => {
      try {
        const fp = getFingerprint();
        const { data } = await axios.get(`${API}/ratings/${recipeId}`, {
          params: { fingerprint: fp },
        });
        if (cancelled) return;
        setAgg({ avg: data.avg, count: data.count });
        setMy(data.my_rating || 0);
        if (onChange) onChange({ avg: data.avg, count: data.count });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('RatingStars: failed to load rating', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeId, onChange]);

  const submit = useCallback(
    async (rating) => {
      if (submitting || !recipeId) return;
      setSubmitting(true);
      const prev = my;
      setMy(rating); // optimistic
      try {
        const { data } = await axios.post(`${API}/ratings`, {
          recipe_id: recipeId,
          rating,
          fingerprint: getFingerprint(),
        });
        setAgg({ avg: data.avg, count: data.count });
        if (onChange) onChange({ avg: data.avg, count: data.count });
        toast.success(
          prev ? `Updated your rating to ${rating} ★` : `Thanks! You rated it ${rating} ★`,
          { duration: 2500 }
        );
      } catch {
        setMy(prev); // rollback
        toast.error('Could not save rating. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, recipeId, my, onChange]
  );

  const display = hover || my;
  const fmt = (n) => (Number.isInteger(n) ? n.toFixed(1) : n.toFixed(1));

  return (
    <div className="flex items-center gap-3" data-testid="rating-stars">
      <div
        className="flex items-center gap-1"
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= display;
          return (
            <button
              key={n}
              type="button"
              onClick={() => submit(n)}
              onMouseEnter={() => setHover(n)}
              disabled={submitting}
              aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
              data-testid={`rate-${n}-star`}
              className="p-0.5 rounded transition-transform hover:scale-110 disabled:opacity-50"
            >
              <Star
                className={`w-6 h-6 transition-colors ${
                  filled ? 'fill-[#FBBF24] text-[#F97316]' : 'text-[#1C1917]/25'
                }`}
              />
            </button>
          );
        })}
      </div>
      <div className="font-['Outfit'] text-sm text-[#1C1917]/70">
        {agg.count > 0 ? (
          <>
            <span className="font-semibold text-[#1C1917]">{fmt(agg.avg)}</span>
            <span className="text-[#1C1917]/55"> · {agg.count} rating{agg.count !== 1 ? 's' : ''}</span>
          </>
        ) : (
          <span className="text-[#1C1917]/55">Be the first to rate</span>
        )}
      </div>
    </div>
  );
};
